package com.iregained.site.components.sections.promo.sections

import androidx.compose.runtime.*
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.argb
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Sup
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.SMOOTH
import org.w3c.dom.ScrollBehavior
import org.w3c.dom.ScrollIntoViewOptions
import kotlin.time.Duration.Companion.milliseconds

// ==========================================
// KEYFRAME ANIMATIONS
// ==========================================

val TextSlideUpKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY(30.px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

val ButtonPopKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).scale(0.5f).translateY(20.px) }
    70.percent { Modifier.opacity(1).scale(1.05f).translateY(-5.px) }
    100.percent { Modifier.scale(1f).translateY(0.px) }
}

val ImageSqueezeKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).scale(1.1f).translateY(20.px) }
    100.percent { Modifier.opacity(1).scale(1f).translateY(0.px) }
}

// ==========================================
// MODIFIER EXTENSIONS
// ==========================================

@Composable
private fun Modifier.staggeredText(delayMs: Int): Modifier = this.animation(
    TextSlideUpKeyframes.toAnimation(
        duration = 0.8.s,
        timingFunction = AnimationTimingFunction.EaseOut,
        delay = delayMs.ms,
        fillMode = AnimationFillMode.Both
    )
)

@Composable
private fun Modifier.staggeredButtonPop(delayMs: Int): Modifier = this.animation(
    ButtonPopKeyframes.toAnimation(
        duration = 0.8.s,
        timingFunction = AnimationTimingFunction.EaseOut,
        delay = delayMs.ms,
        fillMode = AnimationFillMode.Both
    )
)

@Composable
private fun Modifier.staggeredImageSqueeze(delayMs: Int): Modifier = this.animation(
    ImageSqueezeKeyframes.toAnimation(
        duration = 1.2.s,
        timingFunction = AnimationTimingFunction.EaseOut,
        delay = delayMs.ms,
        fillMode = AnimationFillMode.Both
    )
)

@Composable
fun PromoHeroSection() {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    // Interactive hover states
    var isButtonHovered by remember { mutableStateOf(false) }
    var isImageHovered by remember { mutableStateOf(false) }

    // Image Slideshow State
    val deviceImages = listOf(
        Res.Images.Promo.DEVICE_1,
        Res.Images.Promo.DEVICE_2,
        Res.Images.Promo.DEVICE_3,
    )
    var currentImageIndex by remember { mutableStateOf(0) }

    // Slideshow Effect: Cycles images every 3 seconds
    LaunchedEffect(Unit) {
        while (true) {
            delay(3000.milliseconds)
            currentImageIndex = (currentImageIndex + 1) % deviceImages.size
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth().backgroundColor(palette.brand.primary ?: Colors.DarkBlue)
            .padding(topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs()
        ) {
            SimpleGrid(
                numColumns(1, lg = 2), modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(40.px)
            ) {
                // Left: High Impact Text & CTA
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = if (isDesktop) Alignment.Start else Alignment.CenterHorizontally
                ) {
                    Div(
                        attrs = Modifier.color(Colors.White).fontSize(18.px).fontWeight(FontWeight.Bold)
                            .margin(bottom = 16.px).letterSpacing(2.px).staggeredText(delayMs = 100).toAttrs()
                    ) {
                        Text("Exclusive Mail Offer")
                    }

                    Div(
                        attrs = Modifier.color(Colors.White).fontSize(if (isDesktop) 54.px else 40.px)
                            .fontWeight(FontWeight.Bold).lineHeight(1.1).margin(bottom = 24.px)
                            .staggeredText(delayMs = 250).toAttrs()
                    ) {
                        Text("To Unlock Your First FREE Month of MyHand")
                        Sup { Text("®") }
                    }

                    Div(
                        attrs = Modifier.color(Colors.White).fontSize(18.px).lineHeight(1.5).margin(bottom = 40.px)
                            .staggeredText(delayMs = 400).toAttrs()
                    ) {
                        Text("Get the first month Free & save up to $999 when you book a demo and sign up for this promotional program. ")
                        Text("See advanced Digital neurotherapeutics (DTx) & personalized mechatronic hand rehabilitation system in a live virtual demo!")
                    }

                    Box(
                        modifier = Modifier.staggeredButtonPop(delayMs = 550).backgroundColor(Colors.White)
                            .color(Colors.Black).padding(topBottom = 24.px, leftRight = 48.px).borderRadius(40.px)
                            .scale(if (isButtonHovered) 1.05f else 1f).cursor(Cursor.Pointer).boxShadow(
                                offsetX = 0.px,
                                offsetY = if (isButtonHovered) 10.px else 0.px,
                                blurRadius = if (isButtonHovered) 20.px else 0.px,
                                color = argb(if (isButtonHovered) 0.2f else 0f, 0f, 0f, 0f)
                            ).transition(
                                Transition.of("transform", 0.2.s), Transition.of("box-shadow", 0.2.s)
                            ).onMouseEnter { isButtonHovered = true }.onMouseLeave { isButtonHovered = false }.onClick {
                                document.getElementById("booking-calendar")?.scrollIntoView(
                                    ScrollIntoViewOptions(behavior = ScrollBehavior.SMOOTH)
                                )
                            }, contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "Book Your Demo Now!",
                            modifier = Modifier.fontWeight(FontWeight.Bold).fontSize(24.px)
                                .color(palette.brand.primary ?: Colors.DarkBlue)
                        )
                    }
                }

                // Right: Product Graphic Slideshow (Card Switch Effect)
                Column(
                    modifier = Modifier.fillMaxWidth().staggeredImageSqueeze(delayMs = 300),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {

                    // The 3D Card Stack Container
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .maxWidth(600.px) // Keeps the stack constrained centrally on wide screens
                            .padding(bottom = 40.px)
                            .scale(if (isImageHovered) 1.02f else 1f)
                            .transition(Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut))
                            .onMouseEnter { isImageHovered = true }
                            .onMouseLeave { isImageHovered = false }
                            .display(DisplayStyle.Grid) // Aligns all children into the same grid cell
                    ) {
                        deviceImages.forEachIndexed { index, src ->

                            val relIndex = (index - currentImageIndex + deviceImages.size) % deviceImages.size

                            val scaleVal = 1f - (relIndex * 0.05f)
                            val translateYVal = (relIndex * 20).px
                            val zIndexVal = 10 - relIndex
                            val opacityVal = if (relIndex == 0) 1f else (1f - relIndex * 0.3f)

                            Image(
                                src = src,
                                alt = "MyHand Device Card",
                                modifier = Modifier
                                    .gridRow("1")
                                    .gridColumn("1")
                                    // Responsive sizing: Always fills width, scales height perfectly to 5:4
                                    .width(100.percent)
                                    .aspectRatio(5, 4)
                                    // Removed the white background modifier entirely
                                    // Set to Cover to cleanly fill the 5:4 ratio
                                    .objectFit(ObjectFit.Cover)
                                    .borderRadius(12.px)
                                    .boxShadow(
                                        offsetX = 0.px,
                                        offsetY = 15.px,
                                        blurRadius = 30.px,
                                        color = argb(0.25f, 0f, 0f, 0f)
                                    )
                                    .zIndex(zIndexVal)
                                    .opacity(opacityVal)
                                    .scale(scaleVal)
                                    .translateY(translateYVal)
                                    .transition(Transition.of("all", 0.6.s, TransitionTimingFunction.EaseInOut))
                            )
                        }
                    }

                    // Dot Indicators dynamically tracking the current card
                    Row(
                        modifier = Modifier.margin(top = 16.px).gap(12.px)
                    ) {
                        deviceImages.indices.forEach { index ->
                            Box(
                                modifier = Modifier
                                    .size(12.px)
                                    .borderRadius(50.percent)
                                    .backgroundColor(Colors.White)
                                    .opacity(if (index == currentImageIndex) 1 else 0.3)
                                    .transition(Transition.of("opacity", 0.3.s, TransitionTimingFunction.EaseOut))
                            )
                        }
                    }
                }
            }
        }
    }
}